import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath, useParams } from "react-router-dom";

import { DeprecatedGlobalAppV1Paths } from "../App/paths";
import { AppBarHeader, BackButtonLink, PageWithHeader } from "../lib";
import { useGetShiftIdWithOrientationRequired } from "../redesign/Shift/OrientationBlock/useGetShiftIdWithOrientationRequired";
import { useGetShiftBlock } from "../redesign/ShiftDiscovery/Blocks/api/useGetShiftBlock";
import {
  isShiftBlockBookedByAgentId,
  transformIntoShiftBlockWithDetails,
} from "../redesign/ShiftDiscovery/Blocks/utils";
import { SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH } from "../redesign/ShiftDiscovery/paths";
import { ShiftBlockCardSkeleton } from "./components/ShiftBlockCardSkeleton";
import { ShiftBlockDetails } from "./components/ShiftBlockDetails";

export function ShiftBlockDetailsPage() {
  const { shiftBlockId, agentId } = useParams<{
    shiftBlockId: string;
    agentId?: string;
  }>();
  const worker = useDefinedWorker();

  const {
    data: shiftBlock,
    isLoading: isShiftBlockLoading,
    isSuccess: isShiftBlockSuccess,
    refetch: refetchShiftBlock,
  } = useGetShiftBlock({ shiftBlockId, agentId }, { enabled: isDefined(shiftBlockId) });

  const shiftIdWithOrientationRequired = useGetShiftIdWithOrientationRequired();

  /**
   * TODO: Transform the shiftBlock data into ShiftBlockWithDetails in useGetShiftBlock query instead of doing it in the component
   * We can use the construct below to achieve this
   * `const response = useGetQuery(...) return { ...response, data: transformedData, }`
   *
   */
  const shiftBlockWithMappedDetails = isShiftBlockSuccess
    ? transformIntoShiftBlockWithDetails(shiftBlock.data, shiftBlock.included)
    : undefined;

  const isShiftBlockBooked =
    isDefined(shiftBlockWithMappedDetails) &&
    isShiftBlockBookedByAgentId(shiftBlockWithMappedDetails, worker.userId);

  const isOrientationBlock = isDefined(shiftBlock?.data?.attributes?.orientationBlockId);

  const isOrientationBlockBooked = isOrientationBlock && isShiftBlockBooked;

  const defaultBackTo =
    isOrientationBlockBooked && isDefined(shiftIdWithOrientationRequired)
      ? generatePath(SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH, {
          shiftId: shiftIdWithOrientationRequired,
        })
      : DeprecatedGlobalAppV1Paths.OPEN_SHIFTS;

  const isShiftBlockNegotiable =
    isDefined(shiftBlock) && Boolean(shiftBlock.data.attributes.isNegotiable);

  return (
    <PageWithHeader
      appBarHeader={
        <AppBarHeader
          title={isShiftBlockBooked ? "Open Block Details" : "My Booked Block Details"}
          leftCta={
            <BackButtonLink
              alwaysUseDefaultBackTo={isOrientationBlockBooked}
              defaultBackTo={defaultBackTo}
            />
          }
        />
      }
    >
      {/**
       * Padding Bottom of 8 is provided so that the "Book This Blocks" which is absolute positioned, does not overshadown the shift list UI
       * And extra padding of 8 (total = 16) is provided so that the "Negotiate pay or schedule" button is on top of the shift list UI
       */}
      <Box sx={{ paddingBottom: isShiftBlockNegotiable ? 16 : 8 }}>
        {isShiftBlockLoading && <ShiftBlockCardSkeleton />}
        {isShiftBlockSuccess && (
          <ShiftBlockDetails
            worker={worker}
            shiftBlock={shiftBlock}
            refetchShiftBlock={refetchShiftBlock}
            isShiftBlockNegotiable={isShiftBlockNegotiable}
          />
        )}
      </Box>
    </PageWithHeader>
  );
}
